import { ChangeEvent, FC, MouseEvent, useRef, useState } from "react";
import {
    IconButton,
    ModalConfirm,
    ModalLauncher,
    Paragraph,
    Stack,
    Table,
    Tooltip,
} from "@contentful/f36-components";
import { CloudUploadIcon, DeleteIcon } from "@contentful/f36-icons";
import { useSDK } from "@contentful/react-apps-toolkit";
import { useDeleteGlossary, useProcessCSV, useUpdateGlossary } from "../hooks";
import { Glossary } from "../lib";
import { useFetchDeeplGlossaries } from "../services";
import { BadGlossaryLocalesWarning } from "./";

interface GlossaryRowProps {
    glossary: Glossary;
    isSaved: boolean;
}

export const GlossaryRow: FC<GlossaryRowProps> = ({ glossary, isSaved }) => {
    const updatedDate = new Date(glossary.lastUpdated);

    const sdk = useSDK();

    const { processCSV } = useProcessCSV();
    const { updateGlossary } = useUpdateGlossary();
    const { deleteGlossary } = useDeleteGlossary();
    const { mutate: glossariesMutation } = useFetchDeeplGlossaries();

    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleUpdateButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDeleteButtonClick = async (event: MouseEvent<HTMLButtonElement>) => {
        const answer: string = await ModalLauncher.open(({ isShown, onClose }) => (
            <ModalConfirm
                intent="negative"
                shouldCloseOnEscapePress={false}
                shouldCloseOnOverlayClick={false}
                isShown={isShown}
                onCancel={() => onClose("Cancel")}
                onConfirm={() => onClose("Confirm")}>
                Glossary <strong>{glossary.name}</strong> will be permanently deleted.
            </ModalConfirm>
        ));

        if (answer === "Cancel") {
            return;
        }

        try {
            setIsDeleting(true);
            await deleteGlossary(glossary.id);
            await glossariesMutation();
            setIsDeleting(false);
            sdk.notifier.success(`Glossary "${glossary.name}" deleted`);
        } catch (error) {
            setIsDeleting(false);

            if (error instanceof Error) {
                sdk.notifier.error(error.message);
            } else {
                sdk.notifier.error(
                    `An unknown error has occurred while deleting glossary "${glossary.name}"`
                );
            }
        }
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }

        try {
            setIsUpdating(true);

            const selectedFile = event.target.files[0];
            if (selectedFile.type !== "text/csv") {
                throw new Error("CSV file expected");
            }

            const { goodGlossaryLocales, badGlossaryLocales } = await processCSV(selectedFile);

            const answer: string = await ModalLauncher.open(({ isShown, onClose }) => (
                <ModalConfirm
                    intent="primary"
                    shouldCloseOnEscapePress={false}
                    shouldCloseOnOverlayClick={false}
                    isShown={isShown}
                    onCancel={() => onClose("Cancel")}
                    onConfirm={() => onClose("Confirm")}>
                    <Stack flexDirection="column" alignItems="start" spacing="spacingXs">
                        <Paragraph>
                            The entries of glossary: <strong>{glossary.name}</strong> will be
                            overwritten with the contents of <em>{selectedFile.name}</em>.
                        </Paragraph>
                        <BadGlossaryLocalesWarning badLocales={badGlossaryLocales} />
                    </Stack>
                </ModalConfirm>
            ));
            if (answer === "Cancel") {
                setIsUpdating(false);
                return;
            }

            await updateGlossary(glossary, selectedFile, goodGlossaryLocales);
            sdk.notifier.success(`Successfully updated glossary "${glossary.name}"`);
        } catch (error) {
            if (error instanceof Error) {
                sdk.notifier.error(error.message);
            } else {
                sdk.notifier.error(
                    `An unknown error has occurred while updating glossary "${glossary.name}"`
                );
            }
        } finally {
            await glossariesMutation();
            setIsUpdating(false);
        }
    };

    return (
        <Table.Row>
            <Table.Cell>
                <b>{glossary.name}</b>
            </Table.Cell>
            <Table.Cell>
                {glossary.locales
                    .map(pair => `${pair.source.toLowerCase()}/${pair.target.toLowerCase()}`)
                    .sort((a, b) => a.localeCompare(b))
                    .join(", ")}
            </Table.Cell>
            <Table.Cell>{glossary.entryCount}</Table.Cell>
            <Table.Cell>
                <Tooltip content={updatedDate.toLocaleString()}>
                    {/* Doesn't show properly without fragment */}
                    <>{updatedDate.toLocaleDateString()}</>
                </Tooltip>
            </Table.Cell>
            <Table.Cell>
                <Stack spacing="spacingXs" justifyContent="right">
                    <IconButton
                        aria-label="Upload new version"
                        icon={<CloudUploadIcon />}
                        isLoading={isUpdating}
                        variant="secondary"
                        size="small"
                        withTooltip
                        onClick={handleUpdateButtonClick}
                    />
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        tabIndex={-1}
                        style={{
                            opacity: 0,
                            position: "absolute",
                            width: 0,
                            height: 0,
                        }}
                    />
                    <IconButton
                        aria-label="Delete glossary"
                        icon={<DeleteIcon />}
                        isDisabled={isSaved}
                        isLoading={isDeleting}
                        variant="negative"
                        size="small"
                        withTooltip
                        tooltipProps={
                            isSaved
                                ? {
                                      content:
                                          "Cannot delete currently selected glossary. Unselect this glossary and save configuration to delete it.",
                                  }
                                : undefined
                        }
                        onClick={handleDeleteButtonClick}
                    />
                </Stack>
            </Table.Cell>
        </Table.Row>
    );
};
